import { apiBlog } from "@/api/blog.api.ts";
import { apiFiles } from "@/api/file.api.ts";
import { VideoEmbed } from "@/components/rich-editor/components/VideoEmbed.tsx";
import { parseVideoUrl } from "@/components/rich-editor/extensions/video-url.ts";
import { RichEditorBodyFileType } from "@/components/rich-editor/rich-editor.types.ts";
import { Box, HtmlViewer, PdfViewer, Skeleton, Text } from "@skailer-com/uikit";
import { memo, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

interface BlogPostBodyProps {
  postId: number;
  content: string;
  isPreview: boolean;
}

interface BlogPostBodyFileProps {
  type: RichEditorBodyFileType;
  postId: number;
  fileUuid: string;
  fileName?: string;
  isPreview: boolean;
}

function BlogPostBodyFile({ type, postId, fileUuid, fileName, isPreview }: BlogPostBodyFileProps) {
  const { t } = useTranslation();
  const [src, setSrc] = useState<string | null>();

  useEffect(() => {
    let isActive = true;
    let objectUrl: string | undefined;

    void (async () => {
      if (isPreview) {
        const { data } = await apiFiles.getPrivate(fileUuid);
        if (!isActive) return;
        if (data) objectUrl = URL.createObjectURL(data instanceof Blob ? data : new Blob([data]));
        setSrc(objectUrl ?? null);
      } else {
        const { data } = await apiBlog.getAttachmentLink(postId, fileUuid);
        if (isActive) setSrc(data?.download_url ?? null);
      }
    })();

    return () => {
      isActive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [postId, fileUuid, isPreview]);

  const sizeClassName = type === "pdf" ? "h-[70vh] w-full" : "aspect-video w-full";

  if (src === undefined) return <Skeleton className={sizeClassName} />;
  if (src === null) {
    return (
      <Box className="rounded-xs bg-dark/5 p-4">
        <Text className="text-danger">
          {t("blog:body_file_unavailable", { name: fileName ?? "" })}
        </Text>
      </Box>
    );
  }
  if (type === "pdf") return <PdfViewer src={src} className={sizeClassName} />;
  return <VideoEmbed source={{ kind: "file", src }} title={fileName} />;
}

function BlogPostBodyNode({
  element,
  postId,
  isPreview,
}: {
  element: HTMLElement;
  postId: number;
  isPreview: boolean;
}) {
  const type = element.getAttribute("data-type");
  const fileUuid = element.getAttribute("data-file-uuid");
  const fileName = element.getAttribute("data-file-name") ?? undefined;
  const src = element.getAttribute("data-src");

  if (fileUuid && (type === "video" || type === "pdf")) {
    return (
      <BlogPostBodyFile
        type={type}
        postId={postId}
        fileUuid={fileUuid}
        fileName={fileName}
        isPreview={isPreview}
      />
    );
  }

  const source = type === "video" && src ? parseVideoUrl(src) : null;
  return source ? <VideoEmbed source={source} title={fileName} /> : null;
}

const FILE_NODE_SELECTOR = 'div[data-type="video"], div[data-type="pdf"]';

// A re-render of HtmlViewer rewrites its innerHTML and detaches the portal targets.
const BlogPostBodyHtml = memo(function BlogPostBodyHtml({ content }: { content: string }) {
  return <HtmlViewer content={content} />;
});

export function BlogPostBody({ postId, content, isPreview }: BlogPostBodyProps) {
  const [fileElements, setFileElements] = useState<HTMLElement[]>([]);

  const observeFileElements = useCallback((container: HTMLDivElement) => {
    const collect = () => {
      const next = Array.from(container.querySelectorAll<HTMLElement>(FILE_NODE_SELECTOR));
      setFileElements((current) =>
        current.length === next.length && current.every((element, i) => element === next[i])
          ? current
          : next
      );
    };
    collect();
    const observer = new MutationObserver(collect);
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div ref={observeFileElements}>
        <BlogPostBodyHtml content={content} />
      </div>
      {fileElements.map((element, index) =>
        createPortal(
          <BlogPostBodyNode element={element} postId={postId} isPreview={isPreview} />,
          element,
          index
        )
      )}
    </>
  );
}
